import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as activityLogsApi from '../../api/activityLogs'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Badge from '../../components/common/Badge'
import Spinner from '../../components/common/Spinner'
import Button from '../../components/common/Button'
import { ClockIcon, FunnelIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline'

/**
 * Activity Logs Page
 * View all system activity with filtering options
 */
export default function ActivityLogs() {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    entity_type: '',
    action: '',
    user_id: '',
    date_from: '',
    date_to: '',
  })

  const [showFilters, setShowFilters] = useState(false)

  // Fetch activity logs
  const { data, isLoading } = useQuery({
    queryKey: ['activity-logs', filters],
    queryFn: () => activityLogsApi.getActivityLogs(filters),
  })

  // Delete logs mutation
  const deleteViewedLogsMutation = useMutation({
    mutationFn: () => activityLogsApi.deleteByAction('viewed'),
    onSuccess: (result) => {
      queryClient.invalidateQueries(['activity-logs'])
      showToast(`Successfully deleted ${result.deleted_count} 'viewed' activity logs`, 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to delete logs', 'error')
    },
  })

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }))
  }

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 50,
      entity_type: '',
      action: '',
      user_id: '',
      date_from: '',
      date_to: '',
    })
  }

  const hasActiveFilters = filters.entity_type || filters.action || filters.user_id || filters.date_from || filters.date_to

  const handleDeleteViewedLogs = () => {
    if (window.confirm('Are you sure you want to delete all "viewed" activity logs? This action cannot be undone.')) {
      deleteViewedLogsMutation.mutate()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Activity Logs</h1>
          <p className="mt-2 text-gray-600">Complete history of all system activities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteViewedLogs}
            loading={deleteViewedLogsMutation.isPending}
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Clean Up Viewed Logs
          </Button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="h-5 w-5" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <Badge variant="primary" size="sm">
                {Object.values(filters).filter(v => v && v !== 1 && v !== 50).length}
              </Badge>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Entity Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entity Type
                </label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  <option value="contract">Contract</option>
                  <option value="trainer">Trainer</option>
                  <option value="team">Team</option>
                  <option value="payment">Payment</option>
                  <option value="signing_link">Signing Link</option>
                </select>
              </div>

              {/* Action Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action
                </label>
                <select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Actions</option>
                  <optgroup label="Contract Actions">
                    <option value="contract_created">Contract Created</option>
                    <option value="contract_updated">Contract Updated</option>
                    <option value="contract_deleted">Contract Deleted</option>
                    <option value="contract_signed">Contract Signed</option>
                    <option value="contract_signed_via_token">Contract Signed (via link)</option>
                    <option value="signing_link_sent">Signing Link Sent</option>
                    <option value="contract_file_uploaded">File Uploaded</option>
                  </optgroup>
                  <optgroup label="Email Actions">
                    <option value="email_sent">Email Sent</option>
                    <option value="email_sent_success">Email Delivered</option>
                    <option value="email_sent_failed">Email Failed</option>
                    <option value="email_viewed">Email Viewed</option>
                    <option value="emails_history_viewed">Email History Viewed</option>
                    <option value="email_stats_viewed">Email Stats Viewed</option>
                  </optgroup>
                  <optgroup label="User Actions">
                    <option value="user_created">User Created</option>
                    <option value="user_updated">User Updated</option>
                    <option value="user_deleted">User Deleted</option>
                    <option value="user_logged_in">User Logged In</option>
                    <option value="user_logged_out">User Logged Out</option>
                    <option value="password_reset_requested">Password Reset Requested</option>
                    <option value="password_reset_completed">Password Reset Completed</option>
                    <option value="password_changed">Password Changed</option>
                  </optgroup>
                  <optgroup label="Settings Actions">
                    <option value="settings_updated">Settings Updated</option>
                    <option value="smtp_test_successful">SMTP Test Success</option>
                    <option value="smtp_test_failed">SMTP Test Failed</option>
                    <option value="graph_test_successful">Graph Test Success</option>
                    <option value="graph_test_failed">Graph Test Failed</option>
                  </optgroup>
                  <optgroup label="Other Actions">
                    <option value="link">Link</option>
                    <option value="unlink">Unlink</option>
                    <option value="mark_paid">Mark Paid</option>
                  </optgroup>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Activity Logs */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Activity History
              {data?.pagination?.total && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({data.pagination.total.toLocaleString()} total)
                </span>
              )}
            </h2>
            {data?.pagination && (
              <p className="text-sm text-gray-600">
                Page {data.pagination.page} of {data.pagination.total_pages}
              </p>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-12">
              <Spinner size="lg" />
            </div>
          ) : data?.data && data.data.length > 0 ? (
            <div className="space-y-3">
              {data.data.map((activity) => (
                <ActivityLogItem key={activity.id} activity={activity} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No activity logs found</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Clear filters to see all activity
                </button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {data?.pagination && data.pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handleFilterChange('page', filters.page - 1)}
            disabled={filters.page === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-gray-700">
            Page {filters.page} of {data.pagination.total_pages}
          </span>
          <button
            onClick={() => handleFilterChange('page', filters.page + 1)}
            disabled={filters.page >= data.pagination.total_pages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

// Activity Log Item Component
function ActivityLogItem({ activity }) {
  const getActivityColor = (action) => {
    const colorMap = {
      'contract_created': 'bg-green-500',
      'create': 'bg-green-500',
      'contract_signed': 'bg-blue-500',
      'sign': 'bg-blue-500',
      'contract_updated': 'bg-yellow-500',
      'update': 'bg-yellow-500',
      'contract_deleted': 'bg-red-500',
      'delete': 'bg-red-500',
      'signing_link_sent': 'bg-purple-500',
      'send': 'bg-purple-500',
      'contract_file_uploaded': 'bg-indigo-500',
      'upload': 'bg-indigo-500',
      'link': 'bg-cyan-500',
      'unlink': 'bg-orange-500',
      'mark_paid': 'bg-emerald-500',
    }
    return colorMap[action] || 'bg-gray-400'
  }

  const getEntityBadgeVariant = (entityType) => {
    const variantMap = {
      contract: 'info',
      trainer: 'success',
      team: 'warning',
      payment: 'primary',
      signing_link: 'secondary',
    }
    return variantMap[entityType] || 'gray'
  }

  return (
    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className={`flex-shrink-0 w-2 h-2 mt-2 ${getActivityColor(activity.action)} rounded-full`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {formatActivityMessage(activity)}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getEntityBadgeVariant(activity.entity_type)} size="sm">
                {activity.entity_type}
              </Badge>
              <span className="text-xs text-gray-500">
                {new Date(activity.created_at).toLocaleString()}
              </span>
            </div>
            {activity.ip_address && (
              <p className="text-xs text-gray-400 mt-1">
                IP: {activity.ip_address}
              </p>
            )}
          </div>
          {activity.user_first_name && (
            <div className="text-right">
              <p className="text-xs font-medium text-gray-700">
                {activity.user_first_name} {activity.user_last_name}
              </p>
              <p className="text-xs text-gray-500">{activity.user_email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Format activity log message (same as in ContractDetail)
function formatActivityMessage(activity) {
  const { action, entity_type, details, user_first_name, user_last_name } = activity
  
  // Build user prefix - API returns flat fields, not nested user object
  const userPrefix = user_first_name && user_last_name
    ? `${user_first_name} ${user_last_name} `
    : 'System '
  
  let entityDesc = ''
  switch (entity_type) {
    case 'contract':
      entityDesc = details?.contract_title || details?.title || `contract #${activity.entity_id}`
      break
    case 'trainer':
      entityDesc = details?.trainer_name || `trainer #${activity.entity_id}`
      break
    case 'team':
      entityDesc = details?.team_name || `team #${activity.entity_id}`
      break
    case 'payment':
      entityDesc = details?.contract_title
        ? `payment for ${details.contract_title}`
        : `payment #${activity.entity_id}`
      break
    case 'signing_link':
      entityDesc = details?.contract_title || 'signing link'
      break
    case 'email':
      entityDesc = details?.email_type || 'email'
      break
    case 'user':
      entityDesc = details?.email || details?.created_user_email || details?.target_user_email || 'user'
      break
    case 'settings':
      entityDesc = 'system settings'
      break
    default:
      entityDesc = `${entity_type} #${activity.entity_id || ''}`
  }
  
  let actionMsg = ''
  switch (action) {
    // Contract actions
    case 'contract_created':
    case 'create':
      actionMsg = `${userPrefix}created ${entityDesc}`
      break
    case 'contract_updated':
    case 'update':
      actionMsg = `${userPrefix}updated ${entityDesc}`
      break
    case 'contract_deleted':
    case 'delete':
      actionMsg = `${userPrefix}deleted ${entityDesc}`
      break
    case 'contract_signed':
    case 'sign':
      actionMsg = `${userPrefix}signed ${entityDesc}`
      break
    case 'contract_signed_via_token':
      actionMsg = `${userPrefix}signed ${details?.contract_title || entityDesc} via signing link`
      break
    case 'signing_link_sent':
    case 'send':
      const count = details?.recipients_count || 1
      actionMsg = `${userPrefix}sent signing link for ${entityDesc} to ${count} trainer${count > 1 ? 's' : ''}`
      break
    case 'contract_file_uploaded':
    case 'upload':
      actionMsg = details?.file_name
        ? `${userPrefix}uploaded file: ${details.file_name}`
        : `${userPrefix}uploaded file to ${entityDesc}`
      break
    case 'contract_status_changed':
      actionMsg = `${userPrefix}changed ${entityDesc} status: ${details?.old_status || 'unknown'} → ${details?.new_status || 'unknown'}`
      break
    
    // Link/unlink actions
    case 'link':
      actionMsg = details?.trainer_name
        ? `${userPrefix}linked trainer ${details.trainer_name} to ${entityDesc}`
        : details?.team_name
        ? `${userPrefix}linked team ${details.team_name} to ${entityDesc}`
        : `${userPrefix}linked ${entityDesc}`
      break
    case 'unlink':
      actionMsg = details?.trainer_name
        ? `${userPrefix}unlinked trainer ${details.trainer_name} from ${entityDesc}`
        : details?.team_name
        ? `${userPrefix}unlinked team ${details.team_name} from ${entityDesc}`
        : `${userPrefix}unlinked ${entityDesc}`
      break
    
    // Payment actions
    case 'mark_paid':
      actionMsg = `${userPrefix}marked ${entityDesc} as paid`
      break
    
    // Email actions
    case 'email_sent':
      const emailType = details?.email_type || 'email'
      const recipient = details?.recipient || details?.recipient_name || 'recipient'
      actionMsg = `${userPrefix}sent ${emailType} email to ${recipient}`
      break
    case 'email_sent_success':
      actionMsg = `Email delivered to ${details?.recipient || 'recipient'} (${details?.email_type || 'unknown type'})`
      break
    case 'email_sent_failed':
      actionMsg = `Failed to send email to ${details?.recipient || 'recipient'}: ${details?.error || 'Unknown error'}`
      break
    case 'email_viewed':
      actionMsg = `${userPrefix}viewed ${details?.email_type || 'email'} sent to ${details?.recipient || 'recipient'}`
      break
    case 'emails_history_viewed':
      actionMsg = `${userPrefix}viewed email history${details?.results_count !== undefined ? ` (${details.results_count} results)` : ''}`
      break
    case 'email_stats_viewed':
      actionMsg = `${userPrefix}viewed email statistics`
      break
    
    // User management actions
    case 'user_created':
      const createdUserName = details?.created_user_name || details?.created_user_email || 'new user'
      const createdUserRole = details?.created_user_role || 'user'
      actionMsg = `${userPrefix}created ${createdUserRole} account for ${createdUserName}`
      break
    case 'user_updated':
      actionMsg = `${userPrefix}updated user ${details?.updated_user_email || entityDesc}`
      break
    case 'user_deleted':
      actionMsg = `${userPrefix}deleted user ${details?.deleted_user_name || details?.deleted_user_email || entityDesc}`
      break
    case 'password_reset_requested':
      actionMsg = `${userPrefix}requested password reset`
      break
    case 'password_reset_completed':
      actionMsg = `${userPrefix}completed password reset`
      break
    case 'password_reset_forced':
      const targetUser = details?.target_user_name || details?.target_user_email || 'user'
      actionMsg = `${userPrefix}forced password reset for ${targetUser}`
      break
    case 'password_changed':
      actionMsg = `${userPrefix}changed password${details?.forced_change ? ' (forced)' : ''}`
      break
    case 'user_logged_in':
      actionMsg = `${userPrefix}logged in`
      break
    case 'user_logged_out':
      actionMsg = `${userPrefix}logged out`
      break
    
    // Settings actions
    case 'settings_updated':
      const updateCount = details?.updated_count || 'multiple'
      actionMsg = `${userPrefix}updated ${updateCount} system settings`
      break
    case 'smtp_test_successful':
      actionMsg = `${userPrefix}successfully tested SMTP configuration`
      break
    case 'smtp_test_failed':
      actionMsg = `${userPrefix}SMTP test failed: ${details?.error || 'Unknown error'}`
      break
    case 'graph_test_successful':
      actionMsg = `${userPrefix}successfully tested Microsoft Graph configuration`
      break
    case 'graph_test_failed':
      actionMsg = `${userPrefix}Microsoft Graph test failed: ${details?.error || 'Unknown error'}`
      break
    case 'settings_reset':
      actionMsg = `${userPrefix}reset system settings to defaults`
      break
    
    // Default fallback
    default:
      actionMsg = `${userPrefix}${action.replace(/_/g, ' ')} on ${entityDesc}`
  }
  
  return actionMsg
}